/**
 * REPL *runner* — the one place that drives a {@link PythonRuntime} for the
 * immediate window and turns a single submission into a {@link ReplEntry}.
 *
 * It is deliberately DOM-free and runtime-agnostic (it only needs `run` +
 * `onOutput`), so it can be unit-tested with a fake runtime: subscribe to
 * output, run the code, capture either the result repr or a friendly error,
 * then always unsubscribe. The view layer renders whatever this returns.
 */
import type { PythonRuntime } from "../pyodide/types";
import type { OutputChunk } from "../pyodide/types";
import { friendlyError } from "./errors";
import type { ReplEntry } from "./model";

/** Pull a human-readable message out of whatever `run` rejected with. */
function messageOf(thrown: unknown): string {
  if (thrown instanceof Error) return thrown.message;
  if (typeof thrown === "string") return thrown;
  try {
    return String(thrown);
  } catch {
    return "Unknown error";
  }
}

/**
 * Render a Python return value for inline display. Pyodide converts simple
 * values (numbers, strings, booleans) to JS for us; anything exotic still
 * stringifies to *something* useful. `undefined`/`null` (Python `None` or a
 * statement) yields no result line.
 */
function formatResult(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  try {
    return String(value);
  } catch {
    return null;
  }
}

/**
 * Run one submission through the runtime and capture the full outcome.
 *
 * Never throws: a Python-side error is caught and converted to a friendly
 * message on the returned entry, so the caller (the view) only ever deals with
 * plain data.
 */
export async function runCell(
  runtime: PythonRuntime,
  source: string,
): Promise<ReplEntry> {
  const outputs: OutputChunk[] = [];
  const unsubscribe = runtime.onOutput((chunk: OutputChunk) => {
    outputs.push(chunk);
  });

  let result: string | null = null;
  let error: ReplEntry["error"] = null;

  try {
    const value = await runtime.run(source);
    result = formatResult(value);
  } catch (thrown) {
    error = friendlyError(messageOf(thrown));
  } finally {
    unsubscribe();
  }

  return { input: source, outputs, result, error, ok: error === null };
}
